import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Dialog, Grid, Paper, Typography, TextField, Chip } from "@mui/material";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../Context/Contexts";
import { IntegrationSystemViewModel, QueryFilter } from "../../Types/ExportTypes";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import LoadingSection from "../../components/LoadingSection";
import AddIntegrationWizard from "../../components/Wizards/Integrations/AddIntegrationWizard";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import ResponsivePagination from "../../components/ResponsivePagination";
import ListCardHeader from "../../components/List/ListCardHeader";
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
const IntegrationListPage: React.FC = () => {
    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { t } = useTranslation();
    const { showLoading, hideLoading, isLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const [items, setItems] = useState<IntegrationSystemViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [query, setQuery] = useCachedListQuery({ page: 0, pageSize: 10, filter: "", filters: [] });
    const [filter, setFilter] = useState<string>("");
    const [filterProperty, setFilterProperty] = useState<string>("");
    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const fetchIntegrations = async () => {
        showLoading();
        try {
            const res = await authService.post<{ items: IntegrationSystemViewModel[], count: number }>(
                "/integrationApi/GetIntegrations",
                query
            );
            setItems(res.items);
            setTotalCount(res.count);
        } finally {
            hideLoading();
        }
    };

    useEffect(() => { fetchIntegrations(); }, [query]);

    type OpenEditArgs = {
        id?: number | null;
        model?: IntegrationSystemViewModel | null;
    };

    const openEdit = async ({ id, model }: OpenEditArgs = {}) => {
        if (model) {
            const integration = await createIntegration(model);
            if (integration?.id) {
                navigate({ path: "/integration/edit", params: { id: integration.id } });
            }
            return;
        }

        if (id) navigate({ path: "/integration/edit", params: { id } });
        else navigate({ path: "/integration/edit" });
    };

    const createIntegration = async (model: IntegrationSystemViewModel) => {
        showLoading();
        try {
            const res = await authService.post<IntegrationSystemViewModel>("/integrationApi/CreateNewIntegrationFromWizard", model);
            showSnackbar(t("dialog.savedSuccessfully"), "success");
            hideLoading();
            return res;
        } catch (error: any) {
            showSnackbar(`${t("dialog.saveErrorGeneric")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            hideLoading();
        }
    }

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        var localFilterProperty = filterProperty;
        //if (!filter) return;
        if (!localFilterProperty?.trim() && filter?.trim()) {
            localFilterProperty = "Default";
        }

        if (localFilterProperty?.trim() && filter?.trim()) {
            const newFilter = buildQueryFilter(localFilterProperty, filter);
            const isDuplicate = query.filters?.some(f =>
                f.property === newFilter.property && f.value === newFilter.value
            );
            if (isDuplicate) return;

            const newFilters = [...query.filters ?? [], newFilter];
            setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
        } else {
            // No filterProperty: just update the query with the raw filter string
            setQuery({ ...query, page: 0, filter });
        }

        setFilter("");
        setFilterProperty("");
    };

    const openWizard = () => setWizardOpen(true);
    const cancelWizard = () => setWizardOpen(false);

    return (
        <CenteredListCard title={t("common.integrations")}>
  
                <Box sx={{ marginBottom: 2 }}>
                    <Button variant="contained" color="success" size="small" onClick={openWizard}>
                        {t("common.create")}
                    </Button>
                </Box>
                <Box
                    component="form"
                    onSubmit={handleSearchSubmit}
                    sx={{
                        display: "flex",
                        gap: 2,
                        marginBottom: 2,
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={5} lg={1}>
                            <TextField
                                select
                                label={t("common.field")}
                                value={filterProperty}
                                onChange={(e) => setFilterProperty(e.target.value)}
                                SelectProps={{ native: true }}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            >
                                <option value="">{t("common.default")}</option>
                                <option value="ext_Description">{t("common.description")}</option>
                                <option value="ext_Id">Id</option>
                                <option value="ext_Tag">{t("common.tag")}</option>
                            </TextField>
                        </Grid>
                        <Grid item xs={7} lg={8}>
                            {(() => {
                                switch (filterProperty) {
                                    case "ext_Id":
                                        return (
                                            <TextField
                                                label={t("common.search")}
                                                variant="outlined"
                                                type="number"
                                                fullWidth
                                                onChange={(event) => setFilter(event.target.value)}
                                                value={filter}
                                                disabled={isLoading}
                                            />
                                        );

                                    default:
                                        return (
                                            <TextField
                                                label={t("common.search")}
                                                variant="outlined"
                                                fullWidth
                                                onChange={(event) => setFilter(event.target.value)}
                                                value={filter}
                                                disabled={isLoading}
                                            />
                                        );
                                }
                            })()}
                        </Grid>
                        <Grid item xs={12} lg={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={isLoading}
                            >
                                {t("common.search")}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                    {query.filters?.map((f, index) => (
                        <Chip
                            key={index}
                            label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                            onDelete={() => {
                                const newFilters = query.filters?.filter((_, i) => i !== index);
                                setQuery({ ...query, filters: newFilters, page: 0 });
                            }}
                            deleteIcon={<DeleteIcon />}
                        />
                    ))}
                </Box>
                <Dialog open={wizardOpen} maxWidth="sm" fullWidth onClose={cancelWizard}>
                    <Box p={3}>
                        <AddIntegrationWizard cancelWizard={cancelWizard} create={openEdit} />
                    </Box>
                </Dialog>
                <LoadingSection loading={isLoading}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {items.map((item) => (
                            <Grid item
                                xs={12}
                                sm={items.length === 1 ? 12 : items.length === 2 ? 6 : 6}
                                md={items.length === 1 ? 12 : items.length === 2 ? 6 : 4}

                                key={item.id}>
                                <Card
                                    onClick={() => openEdit({ id:item.id })}
                                    sx={{
                                        cursor: "pointer",
                                        transition: "box-shadow 0.2s ease-in-out",
                                        "&:hover": {
                                            boxShadow: 4,
                                            backgroundColor: "secondary.main",
                                        },
                                    }}
                                >
                                    <CardContent>
                                        <ListCardHeader
                                            icon={<IntegrationInstructionsIcon fontSize="small" color="action" />}
                                            title={item.description}
                                        />
                                        <Typography variant="body2" color="textSecondary">
                                            {item.id}
                                        </Typography>

                                        <Typography variant="body2" color="textSecondary">
                                            {item.tag && (<>tag: {item.tag}</>)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                        {items.length === 0 && (
                            <Grid item xs={12}>
                                <Typography align="center">{t("common.noItemsFound")}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </LoadingSection>

                {/* Pagination */}
                <ResponsivePagination

                    page={query.page}

                    pageSize={query.pageSize}

                    totalCount={totalCount}

                    onPageChange={handlePageChange}

                />
    
        </CenteredListCard>
    );
};

export default IntegrationListPage;